import React, { useEffect, useState } from "react";
import {
  FaUserEdit,
  FaFileInvoice,
  FaMoneyCheckAlt,
  FaDraftingCompass,
  FaHandshake,
  FaTools,
  FaClipboardCheck,
  FaCalculator,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";

const NAVY = "#343A76";
const TEAL = "#23868a";

// True on a phone-sized screen (the layout then switches to a list)
const NARROW_WIDTH = 600;

const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= NARROW_WIDTH,
  );

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= NARROW_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return narrow;
};

const Home = ({ selectedProject }) => {
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();

  // ============================================================
  // PROJECT STATUS
  // ============================================================

  const isAfterBooking = Boolean(selectedProject?.hasPayment);

  // ============================================================
  // LOGGED-IN USER
  // ============================================================

  let user = {};

  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    user = {};
  }

  const userRole = String(
    user?.role || user?.roleName || localStorage.getItem("userRole") || "",
  )
    .trim()
    .toLowerCase();

  const isAdmin = userRole === "admin";

  // ============================================================
  // ROLE BASED SPECIAL ACCESS
  // ============================================================

  const financeRoles = ["admin", "head_accountant", "accountant"];

  const vendorRoles = [
    "admin",
    "project_manager",
    "head_accountant",
    "accountant",
  ];

  const canSeeFinance = financeRoles.includes(userRole);
  const canSeeVendor = vendorRoles.includes(userRole);

  // ============================================================
  // CARD PERMISSIONS
  // ============================================================

  const canClientDetail = isAdmin || canAccess("home", "clientDetail");

  const canQuote = isAdmin || canAccess("home", "quote");

  const canPaymentDetail =
    isAdmin || canSeeFinance || canAccess("home", "paymentDetail");

  const canDrawings3D = isAdmin || canAccess("home", "drawings3D");

  const canVendorQuote =
    isAdmin || canSeeVendor || canAccess("home", "vendorQuote");

  const canRecce = isAdmin || canAccess("home", "recce");

  const canSiteProgress = isAdmin || canAccess("home", "siteProgress");

  const canVendorEstimate =
    isAdmin || canSeeVendor || canAccess("home", "vendorEstimate");

  // ============================================================
  // MODULE CARDS (same order, same rules, same pages as before)
  // ============================================================

  const modules = [
    {
      key: "client",
      show: canClientDetail,
      path: "/client-detail",
      Icon: FaUserEdit,
      color: "#007bff",
      title: "Client Detail",
      text: "Edit Client Information",
    },
    {
      key: "drawings",
      show: isAfterBooking && canDrawings3D,
      path: "/3dDrawings",
      Icon: FaDraftingCompass,
      color: "#6f42c1",
      title: "Drawings & 3D",
      text: "3D Drawings",
    },
    {
      key: "recce",
      show: isAfterBooking && canRecce,
      path: "/recce-module",
      Icon: FaClipboardCheck,
      color: "#ff5722",
      title: "Recce",
      text: "Manage Recce Updates",
    },
    {
      key: "quote",
      show: canQuote && userRole !== "project_manager",
      path: "/quote",
      Icon: FaFileInvoice,
      color: "#28a745",
      title: "Quote",
      text: "Create / Edit Quote",
    },
    {
      key: "vendorQuote",
      show: isAfterBooking && canVendorQuote,
      path: "/VendorQuote",
      Icon: FaHandshake,
      color: "#17a2b8",
      title: "Vendor Cost & Payments",
      text: "Manage Vendor Costs & Quotes",
    },
    {
      key: "payment",
      show: canPaymentDetail,
      path: "/PaymentDetail",
      Icon: FaMoneyCheckAlt,
      color: "#e0a800",
      title: "P & L",
      text: "Add Payment Information",
    },
    {
      key: "site",
      show: isAfterBooking && canSiteProgress,
      path: "/SiteProgress",
      Icon: FaTools,
      color: "#dc3545",
      title: "Site Progress",
      text: "Track Site Progress Updates",
    },
    {
      key: "vendorEstimate",
      show: isAfterBooking && canVendorEstimate,
      path: "/vendor-estimate",
      Icon: FaCalculator,
      color: "#20c997",
      title: "Vendor Estimate",
      text: "Track Vendor Estimate Updates",
    },
  ].filter((item) => item.show);

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div style={pageStyle}>
      {/* ========================================================
          TOP BAR
      ======================================================== */}

      <div style={topBarStyle}>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={backButtonStyle}
        >
          <span aria-hidden="true" style={{ fontSize: "17px", lineHeight: 1 }}>
            ←
          </span>
          Back
        </button>

        <div style={topTitleStyle}>Project Dashboard</div>

        {/* keeps the title centred on a laptop */}
        {!isNarrow && <div style={{ width: "92px" }} />}
      </div>

      {/* ========================================================
          PROJECT BANNER
      ======================================================== */}

      <div style={bannerStyle}>
        <div aria-hidden="true" style={bannerGlowStyle} />

        <div style={bannerEyebrowStyle}>PROJECT</div>

        <h2 style={bannerNameStyle}>
          {selectedProject?.name || "Project Name"}
        </h2>

        <span
          style={{
            ...statusPillStyle,
            backgroundColor: isAfterBooking
              ? "rgba(40,167,69,0.22)"
              : "rgba(255,255,255,0.16)",
            borderColor: isAfterBooking
              ? "rgba(134,239,172,0.6)"
              : "rgba(255,255,255,0.35)",
          }}
        >
          <span
            style={{
              ...statusDotStyle,
              backgroundColor: isAfterBooking ? "#4ade80" : "#cbd5e1",
            }}
          />
          {isAfterBooking ? "After Booking" : "Before Booking"}
        </span>
      </div>

      {/* ========================================================
          MODULE CARDS
      ======================================================== */}

      {modules.length > 0 && (
        <>
          <div style={sectionLabelStyle}>Modules</div>

          <div
            style={
              isNarrow
                ? {
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "12px",
                    maxWidth: "1100px",
                    margin: "0 auto",
                  }
                : {
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "40px",
                    maxWidth: "1100px",
                    margin: "0 auto",
                  }
            }
          >
            {modules.map((item) => (
              <ModuleCard
                key={item.key}
                item={item}
                horizontal={isNarrow}
                onOpen={() => navigate(item.path)}
              />
            ))}
          </div>
        </>
      )}

      {/* ========================================================
          NO CARD PERMISSION
      ======================================================== */}

      {modules.length === 0 && (
        <div
          style={{
            textAlign: "center",
            marginTop: "40px",
            color: "#64748b",
            fontSize: "15px",
            fontWeight: "600",
          }}
        >
          No modules are available for your current permissions.
        </div>
      )}
    </div>
  );
};

// ============================================================
// MODULE CARD
// ============================================================

const ModuleCard = ({ item, horizontal, onOpen }) => {
  const [active, setActive] = useState(false);
  const { Icon, color, title, text } = item;

  const onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKey}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: horizontal ? "row" : "column",
        alignItems: "center",
        justifyContent: horizontal ? "flex-start" : "center",
        textAlign: horizontal ? "left" : "center",
        gap: horizontal ? "14px" : "16px",
        width: horizontal ? "auto" : "320px",
        height: horizontal ? "auto" : "260px",
        minHeight: horizontal ? "86px" : undefined,
        padding: horizontal ? "14px 16px" : "26px",
        backgroundColor: "#ffffff",
        border: `1px solid ${active ? `${color}66` : "#e8edf3"}`,
        borderRadius: horizontal ? "18px" : "22px",
        boxShadow: active
          ? `0 18px 38px ${color}30`
          : "0 6px 18px rgba(15, 23, 42, 0.07)",
        transform: active && !horizontal ? "translateY(-6px)" : "translateY(0)",
        cursor: "pointer",
        outline: "none",
        boxSizing: "border-box",
        transition:
          "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
      }}
    >
      {/* Coloured edge */}
      <div
        aria-hidden="true"
        style={
          horizontal
            ? {
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: "5px",
                backgroundColor: color,
              }
            : {
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "5px",
                backgroundImage: `linear-gradient(90deg, ${color}, ${color}88)`,
              }
        }
      />

      {/* Icon badge */}
      <div
        style={{
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          width: horizontal ? "52px" : "66px",
          height: horizontal ? "52px" : "66px",
          marginLeft: horizontal ? "4px" : 0,
          marginTop: 0,
          borderRadius: horizontal ? "16px" : "20px",
          backgroundColor: `${color}1c`,
          color,
        }}
      >
        <Icon size={horizontal ? 24 : 30} />
      </div>

      {/* Title + text */}
      <div
        style={{
          flex: horizontal ? 1 : "none",
          minWidth: 0,
          padding: horizontal ? 0 : "0 12px",
        }}
      >
        <h3
          style={{
            margin: "0 0 5px",
            fontSize: horizontal ? "17px" : "23px",
            fontWeight: "800",
            color: NAVY,
            letterSpacing: "-0.2px",
          }}
        >
          {title}
        </h3>

        <p
          style={{
            margin: 0,
            fontSize: horizontal ? "14px" : "16px",
            lineHeight: 1.5,
            color: "#64748b",
          }}
        >
          {text}
        </p>
      </div>

      {/* Arrow */}
      <div
        aria-hidden="true"
        style={{
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          width: "36px",
          height: "36px",
          ...(horizontal
            ? {}
            : { position: "absolute", right: "16px", bottom: "16px" }),
          borderRadius: "50%",
          backgroundColor: active ? color : `${color}14`,
          color: active ? "#ffffff" : color,
          fontSize: "18px",
          fontWeight: "800",
          transition: "background-color 0.25s ease, color 0.25s ease",
        }}
      >
        →
      </div>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const pageStyle = {
  padding: "clamp(12px, 3vw, 30px)",
  width: "100%",
  boxSizing: "border-box",
};

const topBarStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  paddingBottom: "12px",
  marginBottom: "clamp(16px, 3vw, 24px)",
  borderBottom: "1px solid #dbeafe",
};

const backButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  minHeight: "40px",
  padding: "0 16px",
  backgroundColor: "#ffffff",
  color: "#1f2340",
  border: "1px solid #d7dee8",
  borderRadius: "999px",
  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "700",
};

const topTitleStyle = {
  flex: 1,
  textAlign: "center",
  margin: 0,
  fontSize: "clamp(17px, 4.5vw, 22px)",
  fontWeight: "800",
  color: "#111827",
};

const bannerStyle = {
  position: "relative",
  overflow: "hidden",
  maxWidth: "1100px",
  margin: "0 auto clamp(22px, 4vw, 34px)",
  padding: "clamp(18px, 4vw, 26px) clamp(16px, 4vw, 30px)",
  textAlign: "center",
  color: "#ffffff",
  backgroundColor: NAVY,
  backgroundImage: `linear-gradient(120deg, ${NAVY} 0%, #2f4a8c 55%, ${TEAL} 100%)`,
  borderRadius: "24px",
  boxShadow: "0 18px 40px rgba(52, 58, 118, 0.28)",
  boxSizing: "border-box",
};

const bannerGlowStyle = {
  position: "absolute",
  top: "-120px",
  right: "-80px",
  width: "320px",
  height: "320px",
  borderRadius: "50%",
  backgroundImage:
    "radial-gradient(circle, rgba(255,255,255,0.22), transparent 68%)",
  pointerEvents: "none",
};

const bannerEyebrowStyle = {
  position: "relative",
  marginBottom: "8px",
  fontSize: "11px",
  fontWeight: "800",
  letterSpacing: "3px",
  color: "rgba(255,255,255,0.75)",
};

const bannerNameStyle = {
  position: "relative",
  margin: "0 0 12px",
  fontSize: "clamp(20px, 5vw, 30px)",
  fontWeight: "800",
  lineHeight: 1.15,
  letterSpacing: "-0.8px",
  textTransform: "capitalize",
  overflowWrap: "anywhere",
};

const statusPillStyle = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "7px 16px",
  border: "1px solid",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: "700",
};

const statusDotStyle = {
  width: "9px",
  height: "9px",
  borderRadius: "50%",
};

const sectionLabelStyle = {
  maxWidth: "1100px",
  textAlign: "center",
  margin: "0 auto 16px",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "2.5px",
  textTransform: "uppercase",
  color: "#64748b",
};

export default Home;
